import { useState, useCallback } from 'react';
import type { FieldValues, Path, UseFormSetValue, FieldError } from 'react-hook-form';
import type { AddressAutocompleteState, AddressDetails } from '../../types/address.types';

interface FormAddressAutocompleteFieldProps<T extends FieldValues> {
  setValue: UseFormSetValue<T>;
  label: string;
  required?: boolean;
  error?: FieldError;
  placeholder?: string;
  wrapperClassName?: string;
  inputClassName?: string;
  labelClassName?: string;
  defaultValue?: string;
  autocompleteState: AddressAutocompleteState;
  onInputChange: (input: string) => void;
  onSelectSuggestion: (placeId: string) => Promise<AddressDetails | null>;
  streetFieldName?: Path<T>;
  cityFieldName?: Path<T>;
  postalCodeFieldName?: Path<T>;
  latitudeFieldName?: Path<T>;
  longitudeFieldName?: Path<T>;
  loadingText?: string;
  noResultsText?: string;
}

export function FormAddressAutocompleteField<T extends FieldValues>({
  setValue,
  label,
  required,
  error,
  placeholder,
  wrapperClassName = 'mb-3',
  inputClassName = '',
  labelClassName = '',
  defaultValue = '',
  autocompleteState,
  onInputChange,
  onSelectSuggestion,
  streetFieldName = 'street' as Path<T>,
  cityFieldName = 'city' as Path<T>,
  postalCodeFieldName = 'postalCode' as Path<T>,
  latitudeFieldName = 'latitude' as Path<T>,
  longitudeFieldName = 'longitude' as Path<T>,
  loadingText = 'Ładowanie...',
  noResultsText = 'Brak wyników',
}: FormAddressAutocompleteFieldProps<T>) {
  const [inputValue, setInputValue] = useState(defaultValue);
  const [lastSyncedDefault, setLastSyncedDefault] = useState(defaultValue);
  const [isOpen, setIsOpen] = useState(false);

  if (defaultValue !== lastSyncedDefault) {
    setLastSyncedDefault(defaultValue);
    setInputValue(defaultValue);
  }

  const { suggestions, isFetching, isSuccess, selectionError } = autocompleteState;
  const displayedError = selectionError ?? error?.message;
  const hasError = Boolean(displayedError);

  const handleSelect = useCallback(
    async (description: string, placeId: string) => {
      setIsOpen(false);
      setInputValue(description);

      const details = await onSelectSuggestion(placeId);
      if (!details) return;

      const setOptions = { shouldValidate: true, shouldDirty: true } as const;
      setValue(latitudeFieldName, details.latitude as T[Path<T>], setOptions);
      setValue(longitudeFieldName, details.longitude as T[Path<T>], setOptions);
      if (details.street !== undefined) {
        setValue(streetFieldName, (details.street ?? '') as T[Path<T>], setOptions);
      }
      if (details.city !== undefined) {
        setValue(cityFieldName, (details.city ?? '') as T[Path<T>], setOptions);
      }
      if (details.postalCode !== undefined) {
        setValue(postalCodeFieldName, (details.postalCode ?? '') as T[Path<T>], setOptions);
      }
    },
    [onSelectSuggestion, setValue, latitudeFieldName, longitudeFieldName, streetFieldName, cityFieldName, postalCodeFieldName]
  );

  return (
    <div className={wrapperClassName}>
      <label className={`form-label ${required ? 'required' : ''} ${labelClassName}`}>{label}</label>
      <div className="position-relative">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            onInputChange(e.target.value);
            setIsOpen(e.target.value.trim().length >= 3);
          }}
          onFocus={() => {
            if (inputValue.trim().length >= 3 && suggestions.length > 0) setIsOpen(true);
          }}
          onBlur={() => setTimeout(() => setIsOpen(false), 150)}
          placeholder={placeholder}
          className={`form-control ${hasError ? 'is-invalid' : ''} ${inputClassName}`}
          autoComplete="off"
        />
        {isOpen && (isFetching || isSuccess) && (
          <ul
            className="dropdown-menu show w-100"
            style={{ position: 'absolute', top: '100%', left: 0, zIndex: 1050, maxHeight: '200px', overflowY: 'auto' }}>
            {isFetching && suggestions.length === 0 && (
              <li>
                <span className="dropdown-item text-muted">{loadingText}</span>
              </li>
            )}
            {!isFetching && isSuccess && suggestions.length === 0 && (
              <li>
                <span className="dropdown-item text-muted">{noResultsText}</span>
              </li>
            )}
            {suggestions.map((suggestion) => (
              <li key={suggestion.placeId}>
                <button
                  type="button"
                  className="dropdown-item"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSelect(suggestion.description, suggestion.placeId);
                  }}>
                  {suggestion.description}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      {displayedError && <div className="invalid-feedback d-block">{displayedError}</div>}
    </div>
  );
}
